import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
  PropertyFilter,
  PropertyFilterProps,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationItem,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  DEFAULT_ITEMS_PAGE_SIZE,
  ItemsSortField,
  useInvestigationItems,
} from "../../hooks/useInvestigations";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { InvestigationItemsChart } from "./InvestigationItemsChart";
import {
  BreakdownSelection,
  InvestigationPopulationBreakdown,
} from "./InvestigationPopulationBreakdown";
import { TransactionLink } from "./TransactionLink";
import { DlqPkCell } from "./DlqEventModal";
import {
  InvestigationRule,
  formatSnapshotTimestamp,
  formatTimestamp,
  investigationPath,
  isTerminal,
  propertyFilterI18n,
  RULE_FIELDS,
} from "./investigationUtils";
import { GroupsFilterQuery } from "./groupsFilter";

interface InvestigationItemsProps {
  stage: Stage;
  investigationId: string;
  /** "Split N selected…" opens Split by list with the ticked DLQ keys; hidden when unset. */
  onSplitSelected?: (pks: string[]) => void;
  /** Why the owner-only split is unavailable (shown on the disabled button). */
  splitDisabledReason?: string;
  /**
   * The population rule. Columns the rule pins to a single value (every row shares it) are hidden
   * by default so the table leads with what varies per item: PK, transaction, dates.
   */
  rule?: InvestigationRule | null;
  /**
   * Enough of the case to say how much of the population it holds. A closed case released its
   * items on purpose, so the "Held by" column is hidden for it; an active case that holds every
   * item has nothing to say in that column either, so it starts hidden and appears only when some
   * items are held elsewhere. While the worker is still assigning a large population, nothing is
   * held yet — that is progress, not loss, and reads as "Assigning…" rather than "Not held".
   */
  investigation?: Pick<Investigation, "status" | "assignedCount"> & {
    population?: { count?: number } | null;
    assignment?: { status?: string } | null;
  };
}

/** True while the worker is still claiming the population in the background. */
export function assignmentInProgress(
  investigation?: InvestigationItemsProps["investigation"]
): boolean {
  return investigation?.assignment?.status === "PENDING";
}

/** Server maximum page size for the items endpoint. */
const MAX_PAGE_SIZE = 200;
/** Shared with the page-level prefetch so the warmed page is the one this tab opens with. */
const DEFAULT_PAGE_SIZE = DEFAULT_ITEMS_PAGE_SIZE;
const STRING_OPERATORS = ["=", "!=", ":", "!:"];
const TIME_OPERATORS = ["=", "!=", ">", ">=", "<", "<="];

/**
 * PropertyFilter properties for the Items table. Keys are manifest column names, which is what
 * the items endpoint's `filter=` takes; the server filters every manifest row, not just this page.
 */
export const ITEM_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] =
  [
    ...RULE_FIELDS.map((f) => ({
      key: String(f.field),
      propertyLabel: f.label,
      groupValuesLabel: `${f.label} values`,
      operators: STRING_OPERATORS,
    })),
    {
      key: "dlq_pk",
      propertyLabel: "DLQ PK",
      groupValuesLabel: "DLQ PKs",
      operators: STRING_OPERATORS,
    },
    {
      key: "transaction_id",
      propertyLabel: "Transaction ID",
      groupValuesLabel: "Transaction IDs",
      operators: STRING_OPERATORS,
    },
    {
      key: "order_id",
      propertyLabel: "Order ID",
      groupValuesLabel: "Order IDs",
      operators: STRING_OPERATORS,
    },
    {
      key: "dlq_timestamp",
      propertyLabel: "DLQ time",
      groupValuesLabel: "Dates (YYYY-MM-DD, UTC)",
      operators: TIME_OPERATORS,
    },
    {
      key: "transaction_date",
      propertyLabel: "Transaction date",
      groupValuesLabel: "Dates (YYYY-MM-DD)",
      operators: TIME_OPERATORS,
    },
  ];

const ITEM_FILTER_I18N = propertyFilterI18n(
  "Filter population items",
  "Filter by marketplace, use case, queue, PK, transaction, date…"
);

const EMPTY_FILTER: PropertyFilterProps.Query = {
  operation: "and",
  tokens: [],
};

/** The PropertyFilter query in the hook's wire shape; undefined when it has no tokens. */
export const toItemsFilter = (
  query: PropertyFilterProps.Query
): GroupsFilterQuery | undefined =>
  query.tokens.length
    ? {
        operation: query.operation,
        tokens: query.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          value: t.value as unknown,
        })),
      }
    : undefined;

/**
 * The query after clicking a breakdown value: any `=` token on that field is replaced (one value
 * per field reads as "show me these"), everything else the user typed is kept.
 */
export function withValueToken(
  query: PropertyFilterProps.Query,
  selection: BreakdownSelection
): PropertyFilterProps.Query {
  return {
    operation: query.operation,
    tokens: [
      ...query.tokens.filter(
        (t) => !(t.propertyKey === selection.field && t.operator === "=")
      ),
      { propertyKey: selection.field, operator: "=", value: selection.value },
    ],
  };
}

/** Column id → snapshot column a rule predicate can pin. */
const RULE_FIELD_BY_COLUMN: Record<string, string> = {
  queue: "eventsourcekey",
  type: "error_type",
  message: "error_message",
  usecase: "usecase",
  category: "error_category",
  gl: "gl_product_group",
  marketplace: "marketplace_id",
};

/** Columns shown unless the rule already fixes their value for the whole population. */
const DEFAULT_VISIBLE_UNLESS_PINNED = new Set(["queue", "type", "usecase"]);
/** Columns that vary per item and are always shown by default. */
const ALWAYS_VISIBLE_BY_DEFAULT = new Set(["pk", "order", "txDate", "ts"]);
const COLUMN_ORDER = [
  "pk",
  "order",
  "txDate",
  "ts",
  "held",
  "queue",
  "type",
  "message",
  "usecase",
  "category",
  "gl",
  "marketplace",
];

/**
 * Whether the population is only partly held by this (active) case, so the "Held by" column has
 * something to say. Unknown counts read as "all held": the column can always be switched on.
 */
export function partlyHeldElsewhere(
  investigation?: InvestigationItemsProps["investigation"]
): boolean {
  if (!investigation) return false;
  if (isTerminal(investigation.status)) return false;
  // Nothing is held yet while assigning; that says nothing about other cases.
  if (assignmentInProgress(investigation)) return false;
  const total = investigation.population?.count;
  const held = investigation.assignedCount;
  if (typeof total !== "number" || typeof held !== "number") return false;
  return held < total;
}

/**
 * One line on how much of the population this case holds, or null when there is nothing to say
 * (everything held, or counts unknown). A closed case says its items were released, in plain
 * words, so nobody reads an unheld item as something gone wrong; a case still being assigned
 * says so, for the same reason.
 */
export function heldSummary(
  investigation?: InvestigationItemsProps["investigation"]
): string | null {
  if (!investigation) return null;
  if (isTerminal(investigation.status)) {
    return "Items were released when this investigation was closed";
  }
  if (assignmentInProgress(investigation)) {
    return "Assigning items to this investigation in the background, refresh to update";
  }
  const total = investigation.population?.count;
  const held = investigation.assignedCount;
  if (typeof total !== "number" || typeof held !== "number") return null;
  if (held >= total) return null;
  const elsewhere = total - held;
  return `${held.toLocaleString()} of ${total.toLocaleString()} held by this investigation, ${elsewhere.toLocaleString()} held elsewhere or not held`;
}

export function defaultPreferences(
  rule?: InvestigationRule | null,
  investigation?: InvestigationItemsProps["investigation"]
): CollectionPreferencesProps.Preferences {
  const pinned = new Set<string>((rule ?? []).map((p) => String(p.field)));
  const showHeld = partlyHeldElsewhere(investigation);
  return {
    pageSize: DEFAULT_PAGE_SIZE,
    wrapLines: false,
    stripedRows: true,
    contentDisplay: COLUMN_ORDER.map((id) => ({
      id,
      visible:
        ALWAYS_VISIBLE_BY_DEFAULT.has(id) ||
        (id === "held" && showHeld) ||
        (DEFAULT_VISIBLE_UNLESS_PINNED.has(id) &&
          !pinned.has(RULE_FIELD_BY_COLUMN[id])),
    })),
  };
}

/** Column `sortingField` → server-side sort key. Columns not listed here are not sortable. */
export const SORT_FIELD_MAP: Record<string, ItemsSortField> = {
  dlqPk: "dlq_pk",
  eventsourcekey: "eventsourcekey",
  errorType: "error_type",
  errorMessage: "error_message",
  usecase: "usecase",
  transactionId: "transaction_id",
  transactionDate: "transaction_date",
  dlqTimestamp: "dlq_timestamp",
};

const DEFAULT_SORT_COLUMN: TableProps.SortingColumn<InvestigationItem> = {
  sortingField: "dlqTimestamp",
};

const renderPk =
  (stage: Stage) =>
  // eslint-disable-next-line react/display-name
  (item: InvestigationItem) =>
    (
      <DlqPkCell
        stage={stage}
        dlqPk={item.dlqPk}
        transactionId={item.transactionId}
        orderId={item.orderId}
      />
    );

const renderTransaction =
  (stage: Stage) =>
  // eslint-disable-next-line react/display-name
  (item: InvestigationItem) =>
    (
      <TransactionLink
        stage={stage}
        transactionId={item.transactionId}
        orderId={item.orderId}
      />
    );

/**
 * Who holds the item now. Nothing for the usual case (this investigation); a link when another
 * case took it over (a narrower child, a merge survivor, a case that won the claim); "Not held"
 * when nobody does. While the worker is still assigning a large population an unheld item is
 * simply not reached yet, so it reads "Assigning…". No jargon about why — the case page's status
 * already explains a release.
 */
const renderHeldBy =
  (stage: Stage, assigning: boolean) =>
  // eslint-disable-next-line react/display-name
  (item: InvestigationItem) => {
    if (item.currentlyOwned) return "";
    if (item.heldBy?.investigationId) {
      const title = item.heldBy.title || item.heldBy.investigationId;
      return (
        <Link
          href={investigationPath(item.heldBy.investigationId, stage)}
          external
          ariaLabel={`Held by investigation ${title}; open in a new tab`}
        >
          {title}
        </Link>
      );
    }
    return (
      <Box color="text-status-inactive" fontSize="body-s">
        {assigning ? "Assigning…" : "Not held"}
      </Box>
    );
  };

const columnDefinitions = (
  stage: Stage,
  assigning: boolean
): TableProps.ColumnDefinition<InvestigationItem>[] => [
  {
    id: "pk",
    header: "DLQ PK",
    cell: renderPk(stage),
    sortingField: "dlqPk",
    width: 330,
    minWidth: 220,
  },
  {
    id: "order",
    header: "Transaction / order",
    cell: renderTransaction(stage),
    sortingField: "transactionId",
    width: 330,
    minWidth: 220,
  },
  {
    id: "txDate",
    header: "Transaction date",
    cell: (i) => formatTimestamp(i.transactionDate),
    sortingField: "transactionDate",
    width: 190,
    minWidth: 150,
  },
  {
    id: "ts",
    header: "DLQ time",
    cell: (i) => formatTimestamp(i.dlqTimestamp),
    sortingField: "dlqTimestamp",
    width: 190,
    minWidth: 150,
  },
  {
    id: "held",
    header: "Held by",
    cell: renderHeldBy(stage, assigning),
    width: 220,
    minWidth: 140,
  },
  {
    id: "queue",
    header: "Queue",
    cell: (i) => i.eventsourcekey,
    sortingField: "eventsourcekey",
    width: 320,
    minWidth: 200,
  },
  {
    id: "type",
    header: "Error type",
    cell: (i) => i.errorType,
    sortingField: "errorType",
    width: 260,
    minWidth: 140,
  },
  {
    id: "message",
    header: "Templated message",
    cell: (i) => i.errorMessage,
    sortingField: "errorMessage",
    width: 360,
    minWidth: 160,
  },
  {
    id: "usecase",
    header: "Use case",
    cell: (i) => i.usecase || "—",
    sortingField: "usecase",
    width: 160,
    minWidth: 120,
  },
  {
    id: "category",
    header: "Error category",
    cell: (i) => i.errorCategory || "—",
    width: 160,
  },
  {
    id: "gl",
    header: "GL product group",
    cell: (i) => i.glProductGroup || "—",
    width: 150,
  },
  {
    id: "marketplace",
    header: "Marketplace",
    cell: (i) => i.marketplaceId || "—",
    width: 130,
  },
];

/**
 * Items of the frozen population manifest (survives after the DLQ drains).
 *
 * A manifest can hold 100k rows, so paging, filtering and sorting all happen server-side; the
 * browser only ever holds the current page. Selection is kept across pages and filters.
 */
export const InvestigationItems: React.FC<InvestigationItemsProps> = ({
  stage,
  investigationId,
  rule,
  investigation,
  onSplitSelected,
  splitDisabledReason,
}) => {
  const [preferences, setPreferences] =
    useState<CollectionPreferencesProps.Preferences>(() =>
      defaultPreferences(rule, investigation)
    );
  const pageSize = Math.min(
    preferences.pageSize ?? DEFAULT_PAGE_SIZE,
    MAX_PAGE_SIZE
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filterQuery, setFilterQuery] =
    useState<PropertyFilterProps.Query>(EMPTY_FILTER);
  const [sortingColumn, setSortingColumn] =
    useState<TableProps.SortingColumn<InvestigationItem>>(DEFAULT_SORT_COLUMN);
  const [sortingDescending, setSortingDescending] = useState(true);
  const [selectedItems, setSelectedItems] = useState<InvestigationItem[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);

  const sort: ItemsSortField =
    (sortingColumn.sortingField &&
      SORT_FIELD_MAP[sortingColumn.sortingField]) ||
    "dlq_timestamp";

  const loaded = useInvestigationItems(stage, investigationId, {
    offset: (currentPageIndex - 1) * pageSize,
    limit: pageSize,
    filter: toItemsFilter(filterQuery),
    sort,
    desc: sortingDescending,
  });

  // Spinner only when there's nothing current to show: first load, or a page/filter/sort change
  // still displaying the previous key's rows. A background refetch (tab refocus) keeps the rows.
  const showLoading =
    loaded.isLoading || (loaded.isFetching && loaded.isPreviousData);
  const items = loaded.data?.items ?? [];
  const totalCount = loaded.data?.totalCount ?? 0;
  const pagesCount = Math.max(1, Math.ceil(totalCount / pageSize));

  // If the total shrinks (filter/page-size change) below the current page, snap back.
  useEffect(() => {
    if (loaded.data && currentPageIndex > pagesCount) {
      setCurrentPageIndex(pagesCount);
    }
  }, [loaded.data, currentPageIndex, pagesCount]);

  // Value suggestions come from the breakdown. It follows the filter, so keep every value seen
  // while the tab is open: narrowing to BR must not make US disappear from the dropdown.
  const [knownValues, setKnownValues] = useState<Record<string, string[]>>({});
  const breakdown = loaded.data?.fieldBreakdown;
  useEffect(() => {
    if (!breakdown?.length) return;
    setKnownValues((prev) => {
      let changed = false;
      const next = { ...prev };
      breakdown.forEach((b) => {
        const seen = new Set(next[b.field] ?? []);
        (b.values ?? []).forEach((v) => {
          if (v.value && !seen.has(v.value)) {
            seen.add(v.value);
            changed = true;
          }
        });
        next[b.field] = [...seen];
      });
      return changed ? next : prev;
    });
  }, [breakdown]);
  const filteringOptions = useMemo(
    () =>
      Object.entries(knownValues).flatMap(([propertyKey, values]) =>
        values.map((value) => ({ propertyKey, value }))
      ),
    [knownValues]
  );

  const assigning = assignmentInProgress(investigation);
  const columns = useMemo(
    () => columnDefinitions(stage, assigning),
    [stage, assigning]
  );

  if (loaded.error) {
    return (
      <Alert type="error" header="Could not load the frozen population">
        {loaded.error.message}
      </Alert>
    );
  }

  const selectedPks = () =>
    Promise.resolve(
      selectedItems
        .map((i) => i.dlqPk)
        .filter(Boolean)
        .join("\n")
    );

  const filtered = filterQuery.tokens.length > 0;

  const onFilterChange = (query: PropertyFilterProps.Query) => {
    setFilterQuery(query);
    setCurrentPageIndex(1);
  };

  const clearFilter = () => onFilterChange(EMPTY_FILTER);

  /** Narrow the table to a clicked breakdown value and bring the matching rows into view. */
  const selectValue = (selection: BreakdownSelection) => {
    onFilterChange(withValueToken(filterQuery, selection));
    tableRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  const onSortingChange: TableProps["onSortingChange"] = ({ detail }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(!!detail.isDescending);
    setCurrentPageIndex(1);
  };

  const onPreferencesConfirm = (
    next: CollectionPreferencesProps.Preferences
  ) => {
    setPreferences(next);
    if (next.pageSize !== preferences.pageSize) setCurrentPageIndex(1);
  };

  return (
    <SpaceBetween size="l">
      <InvestigationPopulationBreakdown
        fieldBreakdown={loaded.data?.fieldBreakdown}
        loading={showLoading}
        filtered={filtered}
        onSelectValue={selectValue}
      />
      <InvestigationItemsChart
        histogram={loaded.data?.histogram}
        loading={showLoading}
        filtered={filtered}
      />
      <div ref={tableRef} style={{ scrollMarginTop: 16 }}>
        <Table
          columnDefinitions={columns}
          columnDisplay={preferences.contentDisplay}
          items={items}
          trackBy="dlqPk"
          loading={showLoading}
          loadingText="Loading frozen population"
          selectionType="multi"
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => {
            // Keep selections made on other pages / under other filters; only the rows
            // visible on this page are toggled by the table. Dedupe by PK because the
            // table's detail may already carry the off-page rows we passed in.
            const visiblePks = new Set(items.map((i) => i.dlqPk));
            const byPk = new Map<string | undefined, InvestigationItem>();
            selectedItems
              .filter((i) => !visiblePks.has(i.dlqPk))
              .forEach((i) => byPk.set(i.dlqPk, i));
            detail.selectedItems.forEach((i) => byPk.set(i.dlqPk, i));
            setSelectedItems([...byPk.values()]);
          }}
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={onSortingChange}
          resizableColumns
          stickyHeader
          wrapLines={preferences.wrapLines}
          stripedRows={preferences.stripedRows}
          variant="container"
          ariaLabels={{
            selectionGroupLabel: "Population items",
            allItemsSelectionLabel: () => "select all",
            itemSelectionLabel: (_, item) => item.dlqPk ?? "",
          }}
          empty={
            filtered ? (
              <Box textAlign="center" color="inherit">
                <b>No matches</b>
                <Box variant="p" color="inherit">
                  No items match the current filter.
                </Box>
                <Button onClick={clearFilter}>Clear filter</Button>
              </Box>
            ) : (
              <Box textAlign="center" color="inherit">
                <b>No items</b>
                <Box variant="p" color="inherit">
                  The frozen manifest is empty.
                </Box>
              </Box>
            )
          }
          header={
            <Header
              variant="h2"
              counter={
                selectedItems.length
                  ? `(${selectedItems.length}/${totalCount})`
                  : `(${totalCount})`
              }
              description={
                <>
                  Frozen {formatTimestamp(loaded.data?.frozenAt)} from snapshot{" "}
                  {formatSnapshotTimestamp(loaded.data?.snapshotTimestamp)}
                  {heldSummary(investigation) && (
                    <>
                      {" · "}
                      <span data-testid="held-summary">
                        {heldSummary(investigation)}
                      </span>
                    </>
                  )}
                </>
              }
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {onSplitSelected && (
                    <Button
                      iconName="expand"
                      iconAlign="right"
                      disabled={!selectedItems.length || !!splitDisabledReason}
                      disabledReason={
                        splitDisabledReason ??
                        (selectedItems.length
                          ? undefined
                          : "Tick the items to split off.")
                      }
                      onClick={() =>
                        onSplitSelected(
                          selectedItems
                            .map((i) => i.dlqPk)
                            .filter((pk): pk is string => !!pk)
                        )
                      }
                    >
                      {selectedItems.length
                        ? `Split ${selectedItems.length} selected`
                        : "Split selected"}
                    </Button>
                  )}
                  <CopyToClipboard
                    getContent={selectedPks}
                    iconName="copy"
                    disabled={!selectedItems.length}
                    ariaLabel="Copy selected DLQ PKs"
                  >
                    {selectedItems.length
                      ? `Copy ${selectedItems.length} PK${
                          selectedItems.length === 1 ? "" : "s"
                        }`
                      : "Copy PKs"}
                  </CopyToClipboard>
                </SpaceBetween>
              }
            >
              Population items
            </Header>
          }
          filter={
            <PropertyFilter
              query={filterQuery}
              onChange={({ detail }) => onFilterChange(detail)}
              filteringProperties={ITEM_FILTERING_PROPERTIES}
              filteringOptions={filteringOptions}
              i18nStrings={ITEM_FILTER_I18N}
              countText={
                filtered
                  ? `${totalCount.toLocaleString()} match${
                      totalCount === 1 ? "" : "es"
                    }`
                  : ""
              }
              expandToViewport
            />
          }
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              pagesCount={pagesCount}
              onChange={({ detail }) =>
                setCurrentPageIndex(detail.currentPageIndex)
              }
              ariaLabels={{
                nextPageLabel: "Next page",
                previousPageLabel: "Previous page",
                pageLabel: (n) => `Page ${n} of ${pagesCount}`,
              }}
            />
          }
          preferences={
            <CollectionPreferences
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              onConfirm={({ detail }) => onPreferencesConfirm(detail)}
              pageSizePreference={{
                title: "Page size",
                options: [
                  { value: 25, label: "25 items" },
                  { value: 50, label: "50 items" },
                  { value: 100, label: "100 items" },
                  { value: 200, label: "200 items" },
                ],
              }}
              wrapLinesPreference={{
                label: "Wrap lines",
                description: "Wrap long cell content",
              }}
              stripedRowsPreference={{
                label: "Striped rows",
                description: "Alternate row shading",
              }}
              contentDisplayPreference={{
                title: "Columns",
                options: columns.map((c) => ({
                  id: c.id ?? "",
                  label: String(c.header),
                  alwaysVisible: c.id === "pk",
                })),
              }}
            />
          }
          footer={
            loaded.data?.manifestUri ? (
              <Box color="text-body-secondary" fontSize="body-s">
                Manifest:{" "}
                <span style={{ wordBreak: "break-all" }}>
                  {loaded.data.manifestUri}
                </span>
              </Box>
            ) : undefined
          }
        />
      </div>
    </SpaceBetween>
  );
};

export default InvestigationItems;
